import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import { getUserRole } from '../utils/userPermissions'
import apiClient from '../services/apiClient'
import { userInvoiceService } from '../services/userInvoiceService'
import { Invoice } from '../types/financial.types'
import CardForm from '../components/TransparentCheckout/CardForm'
import PixForm from '../components/TransparentCheckout/PixForm'
import { logger } from '../utils/logger'
import {
  ArrowLeft, 
  CreditCard, 
  QrCode, 
  FileText, 
  ExternalLink,
  CheckCircle,
  Copy,
  AlertCircle,
  Clock,
  DollarSign
} from 'lucide-react'

interface PaymentMethod {
  id: string
  name: string
  icon: React.ComponentType<any>
  description: string
  available: boolean
}

const InvoicePayment: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [invoice, setInvoice] = useState<Invoice | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null)
  const [copied, setCopied] = useState<string | null>(null)
  const [processing, setProcessing] = useState(false)
  const [checkoutConfig, setCheckoutConfig] = useState<any>(null)
  const [paymentData, setPaymentData] = useState<any>(null)
  const [showTransparentCheckout, setShowTransparentCheckout] = useState(false)

  // Redux state
  const { user } = useSelector((state: RootState) => state.auth)
  const userRole = getUserRole(user)

  const paymentMethods: PaymentMethod[] = [
    {
      id: 'pix',
      name: 'PIX',
      icon: QrCode,
      description: 'Pagamento instantâneo via PIX',
      available: true
    },
    {
      id: 'credit_card',
      name: 'Cartão de Crédito',
      icon: CreditCard,
      description: 'Visa, Mastercard, Elo, American Express',
      available: true
    },
    {
      id: 'debit_card',
      name: 'Cartão de Débito',
      icon: CreditCard,
      description: 'Débito em conta corrente',
      available: true
    },
    {
      id: 'boleto',
      name: 'Boleto Bancário',
      icon: FileText,
      description: 'Pagamento em qualquer banco',
      available: true
    }
  ]

  useEffect(() => {
    if (id) {
      loadInvoice()
    }
  }, [id, userRole])

  const loadInvoice = async () => {
    try {
      setLoading(true)
      
      // Usar serviço baseado no tipo de usuário
      if (userRole === 'attendant') {
        const response = await userInvoiceService.getInvoiceById(id!)
        setInvoice(response)
        
        // Se a fatura já tem um método de pagamento definido, seleciona automaticamente
        if (response.payment_method) {
          setSelectedMethod(response.payment_method)
        }
      } else {
        const response = await apiClient.get(`/invoices/${id}`)
        setInvoice(response.data.data)
        
        // Se a fatura já tem um método de pagamento definido, seleciona automaticamente
        if (response.data.data.payment_method) {
          setSelectedMethod(response.data.data.payment_method)
        }
      }
    } catch (error) {
      logger.error('Erro ao carregar fatura:', error)
      setError('Erro ao carregar fatura')
    } finally {
      setLoading(false)
    }
  }

  const handlePaymentMethodSelect = (methodId: string) => {
    setSelectedMethod(methodId)
  }

  const handlePayment = async () => {
    if (!selectedMethod || !invoice) return

    try {
      setProcessing(true)
      
      // Usar rota baseada no tipo de usuário
      let response
      if (userRole === 'attendant') {
        response = await apiClient.post(`/user-invoices/${invoice.id}/transparent-payment`, {
          payment_method: selectedMethod
        })
      } else {
        response = await apiClient.post(`/invoices/${invoice.id}/create-payment`, {
          payment_method: selectedMethod
        })
      }
      
      if (response.data.success) {
        const { preference_id, payment_link, qr_code, barcode } = response.data.data
        
        // Configurar checkout transparente
        const config = {
          preferenceId: preference_id,
          amount: invoice.amount,
          currency: invoice.currency,
          payment_link,
          qr_code,
          barcode
        }
        
        setCheckoutConfig(config)
        setShowTransparentCheckout(true)
        
        // Para métodos que não precisam de checkout transparente (boleto)
        if (selectedMethod === 'boleto' && barcode) {
          setPaymentData({
            method: 'boleto',
            barcode,
            amount: invoice.amount,
            currency: invoice.currency
          })
        }
      }
    } catch (error) {
      logger.error('Erro ao processar pagamento:', error)
      setError('Erro ao processar pagamento. Tente novamente.')
    } finally {
      setProcessing(false)
    }
  }


  const handleTransparentPayment = async (cardData: any) => {
    if (!checkoutConfig || !invoice) return

    try {
      setProcessing(true)
      
      // Usar rota baseada no tipo de usuário
      const endpoint = userRole === 'attendant' 
        ? `/user-invoices/${invoice.id}/transparent-payment`
        : `/invoices/${invoice.id}/transparent-payment`
      
      // Processar pagamento transparente
      const response = await apiClient.post(endpoint, {
        payment_method: selectedMethod,
        preference_id: checkoutConfig.preferenceId,
        card_data: cardData
      })
      
      if (response.data.success) {
        navigate(`/invoices/${invoice.id}?payment=success`)
      } else {
        setError(response.data.error || 'Erro ao processar pagamento')
      }
    } catch (error: any) {
      logger.error('Erro ao processar pagamento transparente:', error)
      setError(error.response?.data?.error || 'Erro ao processar pagamento. Tente novamente.')
    } finally {
      setProcessing(false)
    }
  }

  const handlePaymentSuccess = () => {
    navigate(`/invoices/${invoice?.id}?payment=success`)
  }

  const handlePaymentFailure = () => {
    setError('Pagamento não foi processado. Tente novamente.')
    setShowTransparentCheckout(false)
  }

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(type)
      setTimeout(() => setCopied(null), 2000)
    } catch (error) {
      logger.error('Erro ao copiar:', error)
    }
  }

  const formatCurrency = (amount: string, currency: string) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency
    }).format(parseFloat(amount))
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando fatura...</p>
        </div>
      </div>
    )
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Erro ao carregar fatura</h2>
          <p className="text-gray-600 mb-6">{error || 'A fatura solicitada não existe.'}</p>
          <button
            onClick={() => navigate('/financial')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Voltar para Faturas
          </button>
        </div>
      </div>
    )
  }

  if (invoice.status !== 'pending') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {invoice.status === 'paid' ? 'Fatura já foi paga' : 
             invoice.status === 'cancelled' ? 'Fatura foi cancelada' : 
             'Fatura expirada'}
          </h2>
          <p className="text-gray-600 mb-6">
            Esta fatura não pode ser paga no momento.
          </p>
          <button
            onClick={() => navigate(`/invoices/${invoice.id}`)}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Ver Detalhes da Fatura
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(`/invoices/${invoice.id}`)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para Detalhes
          </button>
          
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900">Efetuar Pagamento</h1>
            <p className="text-gray-600 mt-2">Fatura #{invoice.id}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Resumo da Fatura */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border p-6 sticky top-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Resumo da Fatura</h2>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Valor:</span>
                  <span className="text-2xl font-bold text-gray-900 flex items-center">
                    <DollarSign className="w-6 h-6 mr-1" />
                    {formatCurrency(invoice.amount.toString(), invoice.currency)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Vencimento:</span>
                  <span className="text-gray-900 flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    {formatDate(invoice.due_date)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Cliente:</span>
                  <span className="text-gray-900">{invoice.customer_name}</span>
                </div>
                
                <div className="pt-4 border-t">
                  <p className="text-sm text-gray-600">{invoice.description}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Métodos de Pagamento */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Escolha o Método de Pagamento</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {paymentMethods.map((method) => {
                  const Icon = method.icon
                  const isSelected = selectedMethod === method.id
                  
                  return (
                    <button
                      key={method.id}
                      onClick={() => handlePaymentMethodSelect(method.id)}
                      disabled={!method.available}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : method.available
                          ? 'border-gray-200 hover:border-gray-300'
                          : 'border-gray-100 bg-gray-50 cursor-not-allowed'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon className={`w-6 h-6 ${
                          isSelected ? 'text-blue-600' : 
                          method.available ? 'text-gray-600' : 'text-gray-400'
                        }`} />
                        <div className="text-left">
                          <h3 className={`font-medium ${
                            isSelected ? 'text-blue-900' : 
                            method.available ? 'text-gray-900' : 'text-gray-500'
                          }`}>
                            {method.name}
                          </h3>
                          <p className={`text-sm ${
                            isSelected ? 'text-blue-700' : 
                            method.available ? 'text-gray-600' : 'text-gray-400'
                          }`}>
                            {method.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>

              {/* Checkout Transparente */}
              {showTransparentCheckout && checkoutConfig && (
                <div className="mb-8">
                  {selectedMethod === 'pix' && (
                    <PixForm
                      qrCode={checkoutConfig.qr_code || ''}
                      pixCode={checkoutConfig.qr_code || ''}
                      amount={checkoutConfig.amount}
                      currency={checkoutConfig.currency}
                      onPaymentSuccess={handlePaymentSuccess}
                      onPaymentFailure={handlePaymentFailure}
                    />
                  )}

                  {(selectedMethod === 'credit_card' || selectedMethod === 'debit_card') && (
                    <CardForm
                      onPayment={handleTransparentPayment}
                      loading={processing}
                      error={error}
                    />
                  )}

                  {selectedMethod === 'boleto' && paymentData && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
                        <FileText className="w-5 h-5 mr-2" />
                        Boleto Bancário
                      </h3>
                      
                      <div>
                        <div className="bg-white p-4 rounded-lg mb-4">
                          <p className="font-mono text-sm text-gray-900 break-all text-center">
                            {paymentData.barcode}
                          </p>
                        </div>
                        <button
                          onClick={() => copyToClipboard(paymentData.barcode, 'boleto')}
                          className="text-blue-600 hover:text-blue-800 text-sm flex items-center justify-center mx-auto"
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          {copied === 'boleto' ? 'Copiado!' : 'Copiar Código de Barras'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Informações de Pagamento Específicas (modo legado) */}
              {!showTransparentCheckout && selectedMethod && (
                <div className="mb-8">
                  {selectedMethod === 'pix' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-green-900 mb-4 flex items-center">
                        <QrCode className="w-5 h-5 mr-2" />
                        Pagamento via PIX
                      </h3>
                      
                      {invoice.qr_code ? (
                        <div className="text-center">
                          <div className="bg-white p-4 rounded-lg inline-block mb-4">
                            <QrCode className="w-32 h-32 text-gray-400" />
                          </div>
                          <p className="text-sm text-green-700 mb-4">
                            Escaneie o QR Code com seu app de pagamento
                          </p>
                          <button
                            onClick={() => copyToClipboard(invoice.qr_code!, 'pix')}
                            className="text-green-600 hover:text-green-800 text-sm flex items-center justify-center mx-auto"
                          >
                            <Copy className="w-4 h-4 mr-1" />
                            {copied === 'pix' ? 'Copiado!' : 'Copiar Código PIX'}
                          </button>
                        </div>
                      ) : (
                        <div className="text-center">
                          <p className="text-green-700 mb-4">
                            Clique em "Efetuar Pagamento" para gerar o QR Code PIX
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedMethod === 'boleto' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
                        <FileText className="w-5 h-5 mr-2" />
                        Boleto Bancário
                      </h3>
                      
                      {invoice.barcode ? (
                        <div>
                          <div className="bg-white p-4 rounded-lg mb-4">
                            <p className="font-mono text-sm text-gray-900 break-all text-center">
                              {invoice.barcode}
                            </p>
                          </div>
                          <button
                            onClick={() => copyToClipboard(invoice.barcode!, 'boleto')}
                            className="text-blue-600 hover:text-blue-800 text-sm flex items-center justify-center mx-auto"
                          >
                            <Copy className="w-4 h-4 mr-1" />
                            {copied === 'boleto' ? 'Copiado!' : 'Copiar Código de Barras'}
                          </button>
                        </div>
                      ) : (
                        <div className="text-center">
                          <p className="text-blue-700 mb-4">
                            Clique em "Efetuar Pagamento" para gerar o boleto
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {(selectedMethod === 'credit_card' || selectedMethod === 'debit_card') && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center">
                        <CreditCard className="w-5 h-5 mr-2" />
                        Pagamento com Cartão
                      </h3>
                      <p className="text-purple-700 mb-4">
                        Você será redirecionado para uma página segura de pagamento
                      </p>
                      {invoice.payment_link && (
                        <button
                          onClick={() => window.open(invoice.payment_link!, '_blank')}
                          className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center mx-auto"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Abrir Página de Pagamento
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Botão de Pagamento */}
              {!showTransparentCheckout && (
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => navigate(`/invoices/${invoice.id}`)}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handlePayment}
                    disabled={!selectedMethod || processing}
                    className="px-8 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center"
                  >
                    {processing ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Processando...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Efetuar Pagamento
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Botões para checkout transparente */}
              {showTransparentCheckout && (
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => {
                      setShowTransparentCheckout(false)
                      setCheckoutConfig(null)
                      setPaymentData(null)
                    }}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Voltar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default InvoicePayment
